import React, { useState, useContext } from "react";
import { Backdrop, Box, Button, CircularProgress, Link, Typography } from "@mui/material";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import ConfigContext from "../../common/context/ConfigContext";

export default function StripePaymentElement(props) {
  const siteConfig = useContext(ConfigContext);

  const { amount, disabled, validateFields, validate, returnUrl, showPolicy } = props;

  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState("");

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setError(null);

    // Sync checks only — Apple Pay must open within ~1s of this click.
    if (validateFields && !validateFields()) {
      return;
    }

    setProcessing(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
        return;
      }

      const isValid = await validate();
      if (!isValid) {
        setProcessing(false);
        return;
      }

      setProcessing(true);
      await elements.fetchUpdates();

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
      }
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          paymentMethodOrder: ["apple_pay", "google_pay", "card"],
          business: { name: siteConfig.biz || "" },
        }}
      />
      {showPolicy && (
        <Box mt={1} mb={2}>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.95rem" }}
            color="textSecondary"
            gutterBottom
          >
            By selecting the button below, I agree to the{" "}
            <Link underline="always" href="/store-policy">
              Cancelation, Refund & Store Policies
            </Link>
            {" and I acknowledge the "}
            <Link underline="always" href="https://bakestreet.co/privacy-policy">
              Privacy Policy
            </Link>
            {" and "}
            <Link underline="always" href="https://bakestreet.co/terms-of-service">
              Terms of Service
            </Link>
            .
          </Typography>
        </Box>
      )}
      <Box align="center" my={1}>
        {/* Show any error that happens when processing the payment */}
        {error && <Typography color="error">{error}</Typography>}
      </Box>
      <Button
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        fullWidth
        disabled={processing || succeeded || disabled}
      >
        {`Pay ${amount}`}
      </Button>
      {processing && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </form>
  );
}
